import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  IChange,
  IRepetitiveTaskManager,
  ITaskManagement,
  ITaskManagementLog,
} from "../../models/taskManagement.model";
import {
  createTaskManager,
  fetchTaskManager,
  updateTaskStatus,
  updateTaskManagerAssignees,
  fetchTaskMetaData,
  updateDuplicateTask,
  updateTaskManagerApi,
  fetchTaskManagerLogs,
  fetchOnetaskById,
  updateTaskAttachmentsApi,
  updateTaskManagerCommentApi,
  fetchRepetitiveTaskManager,
  fetchOneRepetitiveTask,
  updateRepetitiveTaskManagerApi,
  fetchRepetitiveTaskManagerLogs,
  updateRepetitiveTaskAttachmentsApi,
  updateTaskRequestedEndDateApi,
  fetchTaskNotificationCount,
  fetchAuthorApprovalTasks,
  fetchTaskManagerNotifications,
  fetchTaskManagerUnreadNotificationCount,
  updateStopRepetitiveTaskApi,
} from "../../api/taskManagement.api";
import { ITaskNotification } from "../../models/taskNotifications.model";

interface TaskManagementState {
  statusFilter:
    | ""
    | "ongoing"
    | "waiting for approval"
    | "completed"
    | "duplicate"
    | "overdue";
  tasks: ITaskManagement[];
  repetitiveTasks: IRepetitiveTaskManager[];
  currentTask: ITaskManagement | null;
  currentRepetitiveTask: IRepetitiveTaskManager | null;
  loading: boolean;
  error?: string;
  metadata: {
    type: Record<string, string>;
    priority: Record<string, string>;
  } | null;
  taskManagementLogs: ITaskManagementLog[];
  repetitiveTaskManagementLogs: ITaskManagementLog[];
  logsLoading: boolean;
  notificationCount?: number;
  socketApprovalTasks: ITaskManagement[];
  taskNotification: ITaskNotification[];
  unreadCount?: number;
  notificationLoading?: boolean;
  taskNotificationPagination?: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
  } | null;
}

const initialState: TaskManagementState = {
  statusFilter: "ongoing",
  tasks: [],
  repetitiveTasks: [],
  currentTask: null,
  currentRepetitiveTask: null,
  loading: false,
  error: undefined,
  metadata: null,
  taskManagementLogs: [],
  repetitiveTaskManagementLogs: [],
  logsLoading: false,
  notificationCount: 0,
  socketApprovalTasks: [],
  taskNotification: [],
  unreadCount: undefined,
  notificationLoading: false,
  taskNotificationPagination: null,
};

export const setTaskManagementState = createAction<{
  currentTab?: "your" | "followers" | "author";
  statusFilter?:
    | ""
    | "ongoing"
    | "waiting for approval"
    | "completed"
    | "duplicate"
    | "overdue";
}>("tasks/setState");

export const addTaskManager = createAsyncThunk(
  "tasks/addTask",
  async (
    taskData: FormData | Partial<ITaskManagement>,
    { rejectWithValue }
  ) => {
    try {
      const res = await createTaskManager(taskData);

      return res;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add task");
    }
  }
);

export const getTaskManager = createAsyncThunk<
  { tasks: ITaskManagement[] },
  {
    status: string;
    assignee?: string;
    follower?: string;
    author?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }
>(
  "tasks/getAllTasks",
  async (
    { assignee, follower, author, startDate, endDate, page, limit, status },
    { rejectWithValue }
  ) => {
    try {
      return await fetchTaskManager(
        status,
        assignee,
        follower,
        author,
        startDate,
        endDate,
        page,
        limit
      );
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch tasks");
    }
  }
);

export const getRepetitiveTaskManager = createAsyncThunk<
  {
    repetitiveTasks: IRepetitiveTaskManager[];
  },
  {
    assignee?: string;
    follower?: string;
    startDate?: string;
    endDate?: string;
    page: number;
    limit: number;
    status: string;
  }
>(
  "tasks/getAllRepetitiveTasks",
  async (
    { assignee, follower, startDate, endDate, page, limit, status },
    { rejectWithValue }
  ) => {
    try {
      return await fetchRepetitiveTaskManager(
        status,
        assignee,
        follower,
        startDate,
        endDate,
        page,
        limit
      );
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch repetitive tasks"
      );
    }
  }
);

export const updateTaskAssignees = createAsyncThunk(
  "tasks/updateAssignees",
  async (
    {
      taskIds,
      assignee,
      change,
    }: { taskIds: string[]; assignee: string; change: IChange },
    { rejectWithValue }
  ) => {
    try {
      return await updateTaskManagerAssignees({ taskIds, assignee, change });
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update assignees");
    }
  }
);

export const updateTaskManager = createAsyncThunk<
  ITaskManagement,
  {
    id: string;
    data: ITaskManagement;
    changes?: {
      field: string;
      oldValue: ITaskManagement;
      newValue: ITaskManagement;
    }[];
  }
>("tasks/editTask", async ({ id, data, changes }, { rejectWithValue }) => {
  try {
    return await updateTaskManagerApi(id, data, changes);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to update task");
  }
});

export const updateRepetitiveTaskManager = createAsyncThunk<
  IRepetitiveTaskManager,
  {
    id: string;
    data: IRepetitiveTaskManager;
    changes?: {
      field: string;
      oldValue: IRepetitiveTaskManager;
      newValue: IRepetitiveTaskManager;
    }[];
  }
>(
  "tasks/updateRepetitiveTask",
  async ({ id, data, changes }, { rejectWithValue }) => {
    try {
      return await updateRepetitiveTaskManagerApi(id, data, changes);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update task");
    }
  }
);

export const updateTaskManagementStatus = createAsyncThunk<
  ITaskManagement,
  { taskId: string; approxTime?: string; decision?: "approved" | "rejected" },
  { rejectValue: string }
>(
  "tasks/markComplete",
  async ({ taskId, approxTime, decision }, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateTaskStatus(taskId, approxTime, decision);

      dispatch(getTaskNotificationCount());

      return response;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getTaskMetaData = createAsyncThunk<{
  type: Record<string, string>;
  priority: Record<string, string>;
}>("tasks/getMeta", async (_, { rejectWithValue }) => {
  try {
    const meta = await fetchTaskMetaData();
    return {
      type: meta.type,
      priority: meta.priority,
    };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const updateTaskAsDuplicate = createAsyncThunk(
  "tasks/markDuplicate",
  async (
    {
      currentTaskId,
      duplicateOfTaskId,
      remark,
    }: { currentTaskId: string; duplicateOfTaskId: string; remark: string },

    { rejectWithValue }
  ) => {
    try {
      return await updateDuplicateTask(
        currentTaskId,
        duplicateOfTaskId,
        remark
      );
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to mark duplicate");
    }
  }
);

export const getTaskManagerLogs = createAsyncThunk<
  ITaskManagementLog[],
  { taskId: string }
>("tasks/getTaskLogs", async ({ taskId }, { rejectWithValue }) => {
  try {
    return await fetchTaskManagerLogs(taskId);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to fetch task logs");
  }
});

export const getRepetitiveTaskManagerLogs = createAsyncThunk<
  ITaskManagementLog[],
  { taskId: string }
>("tasks/getRepetitiveTaskLogs", async ({ taskId }, { rejectWithValue }) => {
  try {
    return await fetchRepetitiveTaskManagerLogs(taskId);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to fetch task logs");
  }
});

export const getOnetaskById = createAsyncThunk<
  ITaskManagement,
  { taskId: string }
>("tasks/getOneTask", async ({ taskId }, { rejectWithValue }) => {
  try {
    return await fetchOnetaskById(taskId);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to fetch task");
  }
});

export const getOneRepetitiveTask = createAsyncThunk<
  IRepetitiveTaskManager,
  { taskId: string }
>("tasks/getOneRepetitiveTask", async ({ taskId }, { rejectWithValue }) => {
  try {
    return await fetchOneRepetitiveTask(taskId);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to fetch repetitive task");
  }
});

export const updateTaskAttachments = createAsyncThunk<
  ITaskManagement,
  { taskId: string; payload: FormData | { removeAttachment: string[] } }
>(
  "tasks/updateAttachments",
  async ({ taskId, payload }, { rejectWithValue }) => {
    try {
      return await updateTaskAttachmentsApi(taskId, payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update attachments");
    }
  }
);

export const updateRepetitiveTaskAttachments = createAsyncThunk<
  IRepetitiveTaskManager,
  { taskId: string; payload: FormData | { removeAttachment: string[] } }
>(
  "tasks/updateRepetitiveAttachments",
  async ({ taskId, payload }, { rejectWithValue }) => {
    try {
      return await updateRepetitiveTaskAttachmentsApi(taskId, payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update attachments");
    }
  }
);

export const updateTaskManagerComment = createAsyncThunk<
  ITaskManagement,
  { taskId: string; mentions: string[]; comment: string }
>(
  "tasks/updateComment",
  async ({ taskId, mentions, comment }, { rejectWithValue }) => {
    try {
      return await updateTaskManagerCommentApi(taskId, mentions, comment);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add comment");
    }
  }
);

export const updateTaskRequestedEndDate = createAsyncThunk<
  ITaskManagement,
  {
    taskId: string;
    requestedEndDate?: Date;
    decision?: "approved" | "rejected";
    status: string;
  }
>(
  "tasks/updateRequestedEndDate",
  async (
    { taskId, requestedEndDate, decision, status },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await updateTaskRequestedEndDateApi(
        taskId,
        requestedEndDate,
        decision
      );
      dispatch(getTaskNotificationCount());
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to extend deadline");
    }
  }
);

export const getTaskNotificationCount = createAsyncThunk<number, void>(
  "tasks/getTaskNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchTaskNotificationCount();
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch notification count"
      );
    }
  }
);

export const getAuthorApprovalTasks = createAsyncThunk<
  ITaskManagement[],
  void,
  { rejectValue: string }
>("tasks/getAuthorApprovalTasks", async (_, { rejectWithValue }) => {
  try {
    return await fetchAuthorApprovalTasks();
  } catch (err: any) {
    return rejectWithValue(
      err.message || "Failed to fetch author approval tasks"
    );
  }
});

export const getTaskManagerNotifications = createAsyncThunk<
  { notifications: ITaskNotification[]; pagination: any },
  { page?: number; limit?: number } | void,
  { rejectValue: string }
>(
  "tasks/getTaskManagerNotifications",
  async (opts = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const pageNum = (opts as any)?.page ?? 1;
      const limitNum = (opts as any)?.limit ?? 10;
      return await fetchTaskManagerNotifications(pageNum, limitNum);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch notifications");
    }
  }
);

export const getTaskManagerUnreadNotificationCount = createAsyncThunk<
  number,
  boolean
>(
  "tasks/getTaskManagerUnreadNotificationCount",
  async (isRead = false, { rejectWithValue }) => {
    try {
      return await fetchTaskManagerUnreadNotificationCount(isRead);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch unread count");
    }
  }
);

export const updateStopRepetitiveTask = createAsyncThunk<
  IRepetitiveTaskManager,
  { taskId: string },
  { rejectValue: string }
>("tasks/stopRepetitiveTask", async ({ taskId }, { rejectWithValue }) => {
  try {
    return await updateStopRepetitiveTaskApi(taskId);
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to stop repetitive task");
  }
});

const taskManagementSlice = createSlice({
  name: "taskManagementSlice",
  initialState,
  reducers: {
    recieveUnpushTask: (state, action) => {
      const { _id, status } = action.payload as ITaskManagement;
      const task = state.tasks.find((t) => t._id === _id);

      if (task) {
        task.status = status;
      }
    },
    updateAssigneeTask: (state, action) => {
      const { taskId, assignee } = action.payload;
      const task = state.tasks.find((t) => {
        return t._id?.toString() === taskId.toString();
      });
      console.log(
        "Updating assignee for task:",
        task?.assignee,
        "to",
        assignee
      );
      if (task) {
        task.assignee = assignee;
      }
    },
    UpdateEndDateApproval: (state, action) => {
      const updated = action.payload as ITaskManagement & {
        _id?: string;
      };
      if (!updated || !updated._id) return;

      const merge = (list: ITaskManagement[]) =>
        list.map((t) => (t._id === updated._id ? { ...t, ...updated } : t));

      state.tasks = merge(state.tasks);

      if (state.currentTask?._id === updated._id) {
        state.currentTask = {
          ...state.currentTask,
          ...updated,
        } as ITaskManagement;
      }
    },

    addSocketApprovalTask: (state, action) => {
      const incoming = action.payload as ITaskManagement;
      if (!incoming || !incoming._id) return;

      // upsert: if exists replace, else push
      const idx = state.socketApprovalTasks.findIndex(
        (t) => t._id === incoming._id
      );
      if (idx >= 0) {
        state.socketApprovalTasks[idx] = {
          ...state.socketApprovalTasks[idx],
          ...incoming,
        };
      } else {
        state.socketApprovalTasks.unshift(incoming);
      }
    },

    removeSocketApprovalTask: (state, action) => {
      const taskId = action.payload as string;
      state.socketApprovalTasks = state.socketApprovalTasks.filter(
        (t) => t._id !== taskId
      );
    },

    clearSocketApprovalTasks: (state) => {
      state.socketApprovalTasks = [];
    },

    setNotificationCount: (state, action: { payload: number }) => {
      state.notificationCount = action.payload;
    },

    incrementNotification: (state) => {
      state.notificationCount = (state.notificationCount ?? 0) + 1;
    },

    decrementNotification: (state) => {
      state.notificationCount = Math.max(0, (state.notificationCount ?? 0) - 1);
    },
    setTaskNotification: (state, action) => {
      state.taskNotification.unshift(...action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTaskManagementState, (state, action) => {
        if (action.payload.statusFilter !== undefined) {
          state.statusFilter = action.payload.statusFilter;
        }
      })

      .addCase(addTaskManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(addTaskManager.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addTaskManager.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaskManager.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks;
      })
      .addCase(getTaskManager.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch tasks";
      })
      .addCase(getRepetitiveTaskManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRepetitiveTaskManager.fulfilled, (state, action) => {
        state.loading = false;
        state.repetitiveTasks = action.payload.repetitiveTasks;
      })
      .addCase(getRepetitiveTaskManager.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch repetitive tasks";
      })
      .addCase(updateTaskManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskManager.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
      })
      .addCase(updateTaskManager.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to update task";
      })
      .addCase(updateRepetitiveTaskManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRepetitiveTaskManager.fulfilled, (state, action) => {
        state.loading = false;
        state.repetitiveTasks = state.repetitiveTasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
      })
      .addCase(updateRepetitiveTaskManager.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to update repetitive task";
      })
      .addCase(updateTaskAssignees.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskAssignees.fulfilled, (state, action) => {
        state.loading = false;
        const { assignee } = action.payload;
        state.tasks.forEach((task) => {
          if (action.meta.arg.taskIds.includes(task._id!)) {
            task.assignee = assignee;
          }
        });
      })
      .addCase(updateTaskAssignees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTaskManagementStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskManagementStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTask = action.payload;

        state.tasks = state.tasks.map((task) =>
          task._id === updatedTask._id ? updatedTask : task
        );

        state.socketApprovalTasks = state.socketApprovalTasks.filter(
          (task) =>
            task._id !== updatedTask._id ||
            updatedTask.status === "waiting for approval"
        );
      })
      .addCase(updateTaskManagementStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskMetaData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaskMetaData.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.metadata = payload;
      })
      .addCase(getTaskMetaData.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to load metadata";
      })
      .addCase(updateTaskAsDuplicate.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskAsDuplicate.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
      })
      .addCase(updateTaskAsDuplicate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskManagerLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(getTaskManagerLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.taskManagementLogs = action.payload;
      })
      .addCase(getTaskManagerLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getRepetitiveTaskManagerLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(getRepetitiveTaskManagerLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.repetitiveTaskManagementLogs = action.payload;
      })
      .addCase(getRepetitiveTaskManagerLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getOnetaskById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOnetaskById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTask = action.payload;
      })
      .addCase(getOnetaskById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getOneRepetitiveTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOneRepetitiveTask.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRepetitiveTask = action.payload;
      })
      .addCase(getOneRepetitiveTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTaskAttachments.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskAttachments.fulfilled, (state, action) => {
        state.tasks = state.tasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
        if (state.currentTask?._id === action.payload._id) {
          state.currentTask = action.payload;
        }
      })
      .addCase(updateTaskAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRepetitiveTaskAttachments.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRepetitiveTaskAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.repetitiveTasks = state.repetitiveTasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
        if (state.currentRepetitiveTask?._id === action.payload._id) {
          state.currentRepetitiveTask = action.payload;
        }
      })
      .addCase(updateRepetitiveTaskAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTaskManagerComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskManagerComment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTask = action.payload;
      })
      .addCase(updateTaskManagerComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTaskRequestedEndDate.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskRequestedEndDate.fulfilled, (state, action) => {
        state.loading = false;

        state.currentTask = action.payload;
      })
      .addCase(updateTaskRequestedEndDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskNotificationCount.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaskNotificationCount.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationCount = action.payload;
      })
      .addCase(getTaskNotificationCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAuthorApprovalTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAuthorApprovalTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.socketApprovalTasks = action.payload;
      })
      .addCase(getAuthorApprovalTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskManagerNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaskManagerNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.taskNotification = action.payload.notifications;
        state.taskNotificationPagination = action.payload.pagination;
      })
      .addCase(getTaskManagerNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTaskManagerUnreadNotificationCount.pending, (state) => {
        state.notificationLoading = true;
      })
      .addCase(
        getTaskManagerUnreadNotificationCount.fulfilled,
        (state, action) => {
          state.notificationLoading = false;
          state.unreadCount = action.payload;
        }
      )
      .addCase(
        getTaskManagerUnreadNotificationCount.rejected,
        (state, action) => {
          state.notificationLoading = false;
          state.error = action.payload as string;
        }
      )
      .addCase(updateStopRepetitiveTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateStopRepetitiveTask.fulfilled, (state, action) => {
        state.loading = false;
        state.repetitiveTasks = state.repetitiveTasks.map((task) =>
          task._id === action.payload._id ? action.payload : task
        );
      })
      .addCase(updateStopRepetitiveTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  recieveUnpushTask,
  UpdateEndDateApproval,
  setNotificationCount,
  incrementNotification,
  decrementNotification,
  updateAssigneeTask,
  addSocketApprovalTask,
  removeSocketApprovalTask,
  clearSocketApprovalTasks,
  setTaskNotification,
} = taskManagementSlice.actions;
export default taskManagementSlice.reducer;
